import { createRoot, type Root } from 'react-dom/client';

import type { WallBoard, WallCard, WallConnection, WallShape } from '@walls/store';
import { ExportWallSnapshot } from './ExportWallSnapshot';
import { getWallExportBounds, type ExportBounds } from './exportGeometry';

const exportBackgroundColor = '#ffffff';

interface CaptureWallExportOptions {
  board: WallBoard;
  cards: WallCard[];
  connections: WallConnection[];
  shapes: WallShape[];
}

export interface WallExportCapture {
  bounds: ExportBounds;
  canvas: HTMLCanvasElement;
}

export async function captureWallExport({
  board,
  cards,
  connections,
  shapes,
}: CaptureWallExportOptions): Promise<WallExportCapture> {
  const { default: html2canvas } = await import('html2canvas');
  const bounds = getWallExportBounds(cards, connections, shapes);
  const container = document.createElement('div');
  let root: Root | null = null;

  try {
    container.style.position = 'fixed';
    container.style.left = '-100000px';
    container.style.top = '0';
    container.style.width = `${bounds.width}px`;
    container.style.height = `${bounds.height}px`;
    container.style.overflow = 'hidden';
    container.style.background = exportBackgroundColor;
    document.body.appendChild(container);

    root = createRoot(container);
    root.render(
      <ExportWallSnapshot
        board={board}
        bounds={bounds}
        cards={cards}
        connections={connections}
        shapes={shapes}
      />
    );

    await waitForSnapshot(container);

    const canvas = await html2canvas(container, {
      backgroundColor: exportBackgroundColor,
      logging: false,
      scale: getExportCanvasScale(bounds.width, bounds.height),
      useCORS: true,
    });

    return { bounds, canvas };
  } finally {
    root?.unmount();
    container.remove();
  }
}

export function getCanvasImageData(canvas: HTMLCanvasElement) {
  const jpegData = canvas.toDataURL('image/jpeg', 0.92);

  if (jpegData.startsWith('data:image/jpeg;base64,')) {
    return { dataUrl: jpegData, format: 'JPEG' as const };
  }

  const pngData = canvas.toDataURL('image/png');

  if (pngData.startsWith('data:image/png;base64,')) {
    return { dataUrl: pngData, format: 'PNG' as const };
  }

  throw new Error(
    `Wall export produced invalid canvas image data (${canvas.width}x${canvas.height})`
  );
}

export async function getCanvasImageBlob(canvas: HTMLCanvasElement) {
  return (
    (await canvasToBlob(canvas, 'image/png')) ??
    (await canvasToBlob(canvas, 'image/jpeg', 0.92)) ??
    dataUrlToBlob(getCanvasImageData(canvas).dataUrl)
  );
}

async function waitForSnapshot(container: HTMLElement) {
  await nextAnimationFrame();
  await nextAnimationFrame();
  await waitForExportImageSources(container);

  if ('fonts' in document) {
    await document.fonts.ready;
  }

  await waitForImages(container);
}

function nextAnimationFrame() {
  return new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
}

function waitForImages(container: HTMLElement) {
  const images = Array.from(container.querySelectorAll('img'));

  return Promise.all(
    images.map(
      (image) =>
        new Promise<void>((resolve) => {
          if (image.complete) {
            resolve();
            return;
          }

          image.addEventListener('load', () => resolve(), { once: true });
          image.addEventListener('error', () => resolve(), { once: true });
        })
    )
  );
}

async function waitForExportImageSources(container: HTMLElement) {
  const timeoutMs = 10000;
  const startedAt = Date.now();

  while (
    container.querySelector('[data-export-image-loading="true"]') &&
    Date.now() - startedAt < timeoutMs
  ) {
    await delay(50);
  }
}

function delay(ms: number) {
  return new Promise<void>((resolve) => window.setTimeout(resolve, ms));
}

function getExportCanvasScale(width: number, height: number) {
  const preferredScale = Math.min(window.devicePixelRatio || 1, 2);
  const maxCanvasSide = 12000;
  const maxCanvasArea = 32000000;
  const sideScale = Math.min(maxCanvasSide / width, maxCanvasSide / height);
  const areaScale = Math.sqrt(maxCanvasArea / Math.max(width * height, 1));

  return Math.max(0.1, Math.min(preferredScale, sideScale, areaScale));
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality?: number) {
  return new Promise<Blob | null>((resolve) => {
    canvas.toBlob((blob) => resolve(blob), type, quality);
  });
}

function dataUrlToBlob(dataUrl: string) {
  const [metadata, data] = dataUrl.split(',');
  const mimeMatch = metadata.match(/^data:([^;]+);base64$/);
  const mimeType = mimeMatch?.[1] ?? 'application/octet-stream';
  const bytes = atob(data);
  const byteArray = new Uint8Array(bytes.length);

  for (let index = 0; index < bytes.length; index += 1) {
    byteArray[index] = bytes.charCodeAt(index);
  }

  return new Blob([byteArray], { type: mimeType });
}
